import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import {NgClass} from "@angular/common";
import {TranslateModule, TranslateService} from "@ngx-translate/core";
import {Subscription} from "rxjs";
import {SwiperContainer, register} from "swiper/element/bundle";
import {WorksTypes} from "src/app/enums/Works.enum";
import {WorksModel} from "src/app/models/WorksModel";
import {User} from "src/app/modules/auth/models/User";
import {WorkModalNavigationContext} from "src/app/modules/shared/interfaces/work-modal-navigation.interface";
import {PointerHoverDirective} from "src/app/modules/shared/directives/pointer-hover.directive";
import {WorksService} from "src/app/modules/shared/services/works.service";
import {WorkCardComponent} from "src/app/modules/works/components/work-card/work-card.component";

@Component({
  selector: "app-catalog-trending",
  templateUrl: "./catalog-trending.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {class: "mt-1 block min-w-0 shrink-0"},
  imports: [NgClass, TranslateModule, PointerHoverDirective, WorkCardComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CatalogTrendingComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  @Input() works: WorksModel[] = [];
  @Input({required: true}) user!: User;
  @Input() type: WorksTypes | null = null;
  @Input() revision = 0;
  @Input() isMobile = false;
  @Input() hasMultipleTypes = true;
  @Input({required: true}) modalNavigationContext!: WorkModalNavigationContext;
  @Output() worksLoaded = new EventEmitter<WorksModel[]>();
  @ViewChild("trendingSwiper")
  private swiperRef?: ElementRef<SwiperContainer>;
  @ViewChild("columnGrid") private columnGrid?: ElementRef<HTMLDivElement>;
  @ViewChild("previousButton")
  private previousButton?: ElementRef<HTMLButtonElement>;
  @ViewChild("nextButton") private nextButton?: ElementRef<HTMLButtonElement>;

  readonly gridClasses =
    "grid [--g:clamp(18px,2vw,30px)] gap-[var(--g)] [grid-template-columns:repeat(auto-fill,minmax(min(clamp(146px,9vw,190px),calc((100%_-_var(--g))/2)),1fr))]";
  placeholders = [0, 1];
  isLoading = false;
  hasError = false;
  private requestSubscription?: Subscription;
  private requestedUserId?: string;
  private resizeObserver?: ResizeObserver;
  private destroyed = false;
  private pendingReset = false;
  private displayedWorks: WorksModel[] = [];

  constructor(
    private readonly worksService: WorksService,
    private readonly translate: TranslateService,
    private readonly cdr: ChangeDetectorRef
  ) {
    register();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      this.user?._id !== this.requestedUserId ||
      changes["type"] ||
      changes["revision"]
    ) {
      this.reload();
    }
    const worksChanged =
      this.works.length !== this.displayedWorks.length ||
      this.works.some((work, index) => work !== this.displayedWorks[index]);
    if (worksChanged || changes["isMobile"]) {
      this.displayedWorks = this.works;
      queueMicrotask(() => this.updateSwiper());
    }
  }

  ngAfterViewInit(): void {
    this.resizeObserver = new ResizeObserver(() => this.updateSwiper());
    if (this.columnGrid) {
      this.resizeObserver.observe(this.columnGrid.nativeElement);
    }
    if (this.swiperRef) {
      this.resizeObserver.observe(this.swiperRef.nativeElement);
    }
    queueMicrotask(() => this.updateSwiper());
  }

  reload(): void {
    this.requestSubscription?.unsubscribe();
    if (!this.user?._id) return;
    this.requestedUserId = this.user._id;
    this.isLoading = true;
    this.hasError = false;
    this.pendingReset = true;
    this.requestSubscription = this.worksService
      .getTrendingCatalog(this.user._id, this.type)
      .subscribe({
        next: (works) => {
          this.isLoading = false;
          this.worksLoaded.emit(works);
          this.cdr.markForCheck();
        },
        error: () => {
          this.isLoading = false;
          this.hasError = true;
          this.cdr.markForCheck();
        },
      });
  }

  @HostListener("window:resize")
  updateSwiper(): void {
    const grid = this.columnGrid?.nativeElement;
    const element = this.swiperRef?.nativeElement;
    if (this.destroyed || !grid?.clientWidth || !element) return;

    // Let the browser resolve the same auto-fill grid as catalog results.
    const styles = getComputedStyle(grid);
    const columns = this.isMobile
      ? 2
      : styles.gridTemplateColumns.trim().split(/\s+/).length;
    if (this.placeholders.length !== columns) {
      this.placeholders = Array.from({length: columns}, (_, index) => index);
      this.cdr.markForCheck();
    }
    if (this.isLoading || !element.clientWidth) return;
    const layout = {
      slidesPerView: columns,
      slidesPerGroup: columns,
      spaceBetween: Number.parseFloat(styles.columnGap),
    };
    if (element.swiper) {
      Object.assign(element.swiper.params, layout);
      Object.assign(element.swiper.originalParams, layout);
      element.swiper.update();
      this.resetSwiperPosition();
      return;
    }
    Object.assign(element, {
      ...layout,
      speed: 500,
      watchOverflow: true,
      observer: true,
      observeParents: true,
      observeSlideChildren: true,
      navigation: {
        prevEl: this.previousButton?.nativeElement,
        nextEl: this.nextButton?.nativeElement,
      },
      pagination: false,
      a11y: {
        containerMessage: this.translate.instant("CATALOG_SEARCH_TRENDING"),
        containerRoleDescriptionMessage: this.translate.instant(
          "DASHBOARD_SWIPER_ROLE"
        ),
        itemRoleDescriptionMessage: this.translate.instant(
          "DASHBOARD_SWIPER_ITEM_ROLE"
        ),
        prevSlideMessage: this.translate.instant("PREVIOUS_WORK"),
        nextSlideMessage: this.translate.instant("NEXT_WORK"),
        slideLabelMessage: this.translate.instant("DASHBOARD_SWIPER_SLIDE"),
      },
    });
    element.initialize();
    this.resetSwiperPosition();
  }

  private resetSwiperPosition(): void {
    if (!this.pendingReset) return;
    this.swiperRef?.nativeElement.swiper?.slideTo(0, 0);
    this.pendingReset = false;
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.requestSubscription?.unsubscribe();
    this.resizeObserver?.disconnect();
    this.swiperRef?.nativeElement.swiper?.destroy?.(true, true);
  }
}
